import { useDependencies } from '@servicetitan/react-ioc';
import { SetupDataStore } from '../stores/setupDataStore';
import { FinancingApi, TenantsGetSettingsData } from '@servicetitan/financing-api-client';
import { useQuery } from '@tanstack/react-query';

/**
 * Same tenant-settings fetch as {@link useTenantSettings}, but also surfaces the query's loading
 * state so a readiness gate can wait for it.
 *
 * @returns `data` (the settings, or undefined) and `isLoading` (true only while loading for the
 * first time; false when disabled or errored).
 */
export const useTenantSettingsResult = (): {
    data: TenantsGetSettingsData | undefined;
    isLoading: boolean;
} => {
    const { setupData } = SetupDataStore.useStore();
    const [apiClient] = useDependencies(FinancingApi);

    const settingsResult = useQuery({
        queryKey: ['tenantSettings', setupData?.tenantId],
        queryFn: () => apiClient.tenantsGetSettings(setupData?.tenantId ?? ''),
        enabled: !!setupData?.tenantId,
        staleTime: 5 * 60 * 1000,
    });

    return { data: settingsResult.data?.data, isLoading: settingsResult.isLoading };
};

export const useTenantSettings = (): TenantsGetSettingsData | undefined =>
    useTenantSettingsResult().data;
